import { useAppSelector } from "../state/hooks";
import { FullContestEntryDetails, getFullContestEntryDetailsById } from "../state/slices/contests-slice";
import { Bet } from "interfaces/bet";
import { getGroupState } from "state/slices/group-slice.ts";

interface ContestantPickAllowedCheckResult {
  isLoading: boolean;
  isPickAllowed: boolean;
}

/**
 * Determines if a specific contestant pick is allowed (not locked)
 * within the currently active contest entry. A pick is generally disallowed if
 * it has already been picked and the contest entry is not yet valid.
 * Also provides the loading state of the underlying contest entry bet data.
 */
const useContestContestantPickAllowedCheck = (contestantName: string, contestEntryId: string | undefined): ContestantPickAllowedCheckResult => {
  const group = useAppSelector(getGroupState);

  const isContestGroup = group.contest_group;

  const fullContestEntryDetails: FullContestEntryDetails | undefined =
    useAppSelector(state => getFullContestEntryDetailsById(state, contestEntryId));

  if (!isContestGroup || !contestEntryId) {
    return { isLoading: false, isPickAllowed: true };
  }

  if (!fullContestEntryDetails) {
    return { isLoading: true, isPickAllowed: true };
  }

  const isLoading = fullContestEntryDetails.loadingState === "loading" || fullContestEntryDetails.loadingState === "idle";

  let isPickAllowed: boolean = true;

  if (contestEntryId && fullContestEntryDetails.loadingState === "succeeded") {
    const { calculatedState } = fullContestEntryDetails;
    if (calculatedState) {
      const entryIsNotValid = !calculatedState.isValid;
      const hasAlreadyPickedTheContestant = calculatedState.bets.some((bet: Bet) => bet.contestant_name === contestantName);
      const isLocked = entryIsNotValid && hasAlreadyPickedTheContestant;

      isPickAllowed = !isLocked;
    } else {
      isPickAllowed = false;
    }
  }

  return {
    isLoading,
    isPickAllowed,
  };
};

export default useContestContestantPickAllowedCheck; 